import { useMemo, useState, useEffect } from 'react';
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Table,
  Badge,
  DateCell,
  SearchInput,
  useTable,
  BaseLayout,
  SelectDropdown,
  Spinner,
  Authorizor,
} from '@/components';
import ROLES from '@/constants/roles.constants';
import { useAuth } from '../../hooks/use-auth';
import userIsAuthorized from '../../utils/userIsAuthorized';
import { Button, Card, CardContent } from '@/components/ui';
import { BADGE_TYPES } from '@/constants/common.constants';
import type { UserTableItem } from '@/types/user.types';
import type { AccountListItem } from '@/types/accounts.types';
import type { HierarchyLevelResponse } from '@/types/account-hierarchy.types';
import { getAccounts } from '@/api/account.api';
import { getHierarchyLevels } from '@/api/account-hierarchy.api';
import { downloadUsersAsCsv } from '@/api/user.api';
import { Download, Add } from '@carbon/icons-react';
import { Upload } from 'lucide-react';
import { useToast } from '@/contexts/toast-context';
import { TOAST_TYPES } from '@/constants/common.constants';
import { getEnpsScoreColor } from '@/utils/route-survey';
import { getEnpsScoreClasses } from '@/utils/survey.utils';
import { SURVEY_POSITIVE_SCORE_THRESHOLD } from '../../constants/survey.constants';

interface UserSearchParams {
  // Pagination & Table state (managed by useTable)
  page?: number;
  pageSize?: number;
  sortBy?: string;
  direction?: 'asc' | 'desc';
  searchText?: string;
  // Filters (managed by this component)
  account?: string;
  orgLevel?: string;
  role?: string;
  status?: string;
}

export const Route = createFileRoute('/organizations/')({
  component: Organizations,
  validateSearch: (search: Record<string, unknown>): UserSearchParams => {
    return {
      // Pagination & Table state
      page:
        typeof search.page === 'number'
          ? search.page
          : typeof search.page === 'string'
          ? parseInt(search.page)
          : undefined,
      pageSize:
        typeof search.pageSize === 'number'
          ? search.pageSize
          : typeof search.pageSize === 'string'
          ? parseInt(search.pageSize)
          : undefined,
      sortBy: typeof search.sortBy === 'string' ? search.sortBy : undefined,
      direction:
        search.direction === 'asc' || search.direction === 'desc'
          ? search.direction
          : undefined,
      searchText:
        typeof search.searchText === 'string' ? search.searchText : undefined,
      // Filters
      account: typeof search.account === 'string' ? search.account : undefined,
      orgLevel:
        typeof search.orgLevel === 'string' ? search.orgLevel : undefined,
      role: typeof search.role === 'string' ? search.role : undefined,
      status: typeof search.status === 'string' ? search.status : undefined,
    };
  },
});

interface UserFilters {
  account: string;
  orgLevel: string;
  role: string;
  status: string;
}

const USER_FILTER_VALUES = {
  ACCOUNT: { ALL: 'all' },
  ORGANIZATION_LEVEL: { ALL: 'all' },
  ROLE: {
    ALL: 'all',
    ADMINISTRATOR: 'administrator',
    EXECUTIVE: 'executive',
    MANAGER: 'manager',
    DRIVER: 'driver',
    STAFF: 'staff',
  },
  STATUS: {
    ALL: 'all',
    PROVISIONED: 'provisioned',
    ACTIVATED: 'activated',
    DEACTIVATED: 'deactivated',
  },
};

function Organizations() {
  const navigate = useNavigate();
  const searchParams = useSearch({ from: '/organizations/' });
  const { addToast } = useToast();
  const { user } = useAuth();

  // Determine default organization level based on user role
  const getDefaultOrgLevel = () => {
    // For exec/managers/staff with hierarchy level, default to their hierarchy level
    if (
      userIsAuthorized(user?.role, [
        ROLES.EXECUTIVE,
        ROLES.MANAGER,
        ROLES.STAFF,
      ]) &&
      user?.hierarchyLevel?.uuid
    ) {
      return user.hierarchyLevel.uuid;
    }
    // Otherwise default to ALL
    return USER_FILTER_VALUES.ORGANIZATION_LEVEL.ALL;
  };

  // Initialize filter state from URL search params
  const [filters, setFilters] = useState<UserFilters>({
    account: searchParams.account ?? USER_FILTER_VALUES.ACCOUNT.ALL,
    orgLevel: searchParams.orgLevel ?? getDefaultOrgLevel(),
    role: searchParams.role ?? USER_FILTER_VALUES.ROLE.ALL,
    status: searchParams.status ?? USER_FILTER_VALUES.STATUS.ALL,
  });

  // Update URL when filters change (preserve pagination/sorting/search state)
  useEffect(() => {
    const newSearch: any = { ...searchParams };

    // Include filter params in URL (even when at default values)
    // Account filter - Only for ADMINISTRATOR
    if (userIsAuthorized(user?.role, [ROLES.ADMINISTRATOR])) {
      newSearch.account = filters.account;
    }

    newSearch.orgLevel = filters.orgLevel;
    newSearch.role = filters.role;
    newSearch.status = filters.status;

    navigate({
      to: '/organizations',
      search: newSearch,
      replace: true,
    });
  }, [filters, user?.role, searchParams]);

  // Fetch accounts for dropdown
  const [accounts, setAccounts] = useState<AccountListItem[]>([]);

  // Only fetch accounts if user is admin
  useEffect(() => {
    const fetchAccountsData = async () => {
      try {
        const response = await getAccounts();
        setAccounts(response || []);
      } catch (error) {
        addToast('Failed to fetch accounts.', {
          type: TOAST_TYPES.ERROR,
        });
      }
    };
    if (userIsAuthorized(user?.role, [ROLES.ADMINISTRATOR])) {
      fetchAccountsData();
    }
  }, []);

  // Fetch hierarchy levels for organization level filter
  const [hierarchyLevels, setHierarchyLevels] = useState<
    HierarchyLevelResponse[]
  >([]);

  useEffect(() => {
    const fetchHierarchyLevels = async () => {
      let accountToFetch: string | undefined;

      // For administrators: fetch based on selected account
      if (userIsAuthorized(user?.role, [ROLES.ADMINISTRATOR])) {
        // Only fetch if a specific account is selected
        if (filters.account !== USER_FILTER_VALUES.ACCOUNT.ALL) {
          accountToFetch = filters.account;
        } else {
          // If "All Accounts" is selected, reset org level filter and clear hierarchy
          setHierarchyLevels([]);
          if (filters.orgLevel !== USER_FILTER_VALUES.ORGANIZATION_LEVEL.ALL) {
            setFilters((prev) => ({
              ...prev,
              orgLevel: USER_FILTER_VALUES.ORGANIZATION_LEVEL.ALL,
            }));
          }
          return;
        }
      } else if (
        userIsAuthorized(user?.role, [
          ROLES.EXECUTIVE,
          ROLES.MANAGER,
          ROLES.STAFF,
        ])
      ) {
        // For executives/managers/staff: use placeholder to fetch their account's hierarchy
        accountToFetch = 'placeholder';
      }

      if (accountToFetch) {
        try {
          const response = await getHierarchyLevels(accountToFetch);
          const fetchedLevels = response.levels || [];
          setHierarchyLevels(fetchedLevels);

          // If no hierarchy exists, reset org level filter to "All"
          if (fetchedLevels.length === 0) {
            if (
              filters.orgLevel !== USER_FILTER_VALUES.ORGANIZATION_LEVEL.ALL
            ) {
              setFilters((prev) => ({
                ...prev,
                orgLevel: USER_FILTER_VALUES.ORGANIZATION_LEVEL.ALL,
              }));
            }
          }
        } catch (error) {
          addToast('Failed to fetch organization levels.', {
            type: TOAST_TYPES.ERROR,
          });
          setHierarchyLevels([]);
          // Reset org level filter on error
          if (filters.orgLevel !== USER_FILTER_VALUES.ORGANIZATION_LEVEL.ALL) {
            setFilters((prev) => ({
              ...prev,
              orgLevel: USER_FILTER_VALUES.ORGANIZATION_LEVEL.ALL,
            }));
          }
        }
      }
    };

    fetchHierarchyLevels();
  }, [filters.account, user?.hierarchyLevel]);

  // Helper function to flatten hierarchy levels into dropdown options with indentation
  const flattenHierarchyToOptions = (
    levels: HierarchyLevelResponse[],
    depth = 0,
  ): Array<{ value: string; label: string }> => {
    const options: Array<{ value: string; label: string }> = [];

    levels.forEach((level) => {
      const indent = '\u00A0\u00A0'.repeat(depth); // Two non-breaking spaces per level
      options.push({
        value: level.uuid,
        label: `${indent}${level.name}`,
      });

      if (level.children && level.children.length > 0) {
        options.push(...flattenHierarchyToOptions(level.children, depth + 1));
      }
    });

    return options;
  };

  const hierarchyOptions = useMemo(
    () => flattenHierarchyToOptions(hierarchyLevels),
    [hierarchyLevels],
  );

  // State for triggering data refetch
  const [refetchData, setRefetchData] = useState(false);

  // Use the useTable hook for server-side pagination, sorting, and filtering
  const {
    data,
    pageCount,
    totalRowsCount,
    isLoading,
    paginationState,
    setPaginationState,
  } = useTable<UserTableItem>({
    apiUrl: '/users',
    initialPaginationState: {
      page: 1,
      pageSize: 50,
      sortBy: 'dateAdded',
      direction: 'desc',
      searchText: '',
    },
    additionalParams: {
      account_uuid:
        filters.account !== USER_FILTER_VALUES.ACCOUNT.ALL
          ? filters.account
          : undefined,
      organization_level_uuid:
        filters.orgLevel !== USER_FILTER_VALUES.ORGANIZATION_LEVEL.ALL
          ? filters.orgLevel
          : undefined,
      role:
        filters.role !== USER_FILTER_VALUES.ROLE.ALL ? filters.role : undefined,
      status:
        filters.status !== USER_FILTER_VALUES.STATUS.ALL
          ? filters.status
          : undefined,
    },
    transformResponse: (res) => res.data,
    onError: () => {
      addToast('Failed to fetch users', {
        type: TOAST_TYPES.ERROR,
      });
    },
    dataFetchControl: {
      reFetchData: refetchData,
      setRefetchData: setRefetchData,
    },
  });

  // Define table columns
  const columns = useMemo<ColumnDef<UserTableItem>[]>(() => {
    const baseColumns: ColumnDef<UserTableItem>[] = [
      {
        header: 'Account',
        accessorKey: 'accountName',
        meta: {
          allowedRoles: [ROLES.ADMINISTRATOR],
        },
        cell: ({ row }) => {
          return row.original.accountUuid ? (
            <a
              href={`/accounts/${row.original.accountUuid}`}
              onClick={(e) => {
                if (e.ctrlKey || e.metaKey) {
                  e.stopPropagation();
                } else {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate({ to: `/accounts/${row.original.accountUuid}` });
                }
              }}
              className="link"
            >
              {row.original.accountName}
            </a>
          ) : (
            <span>{row.original.accountName || 'N/A'}</span>
          );
        },
      },

      {
        header: 'Name',
        accessorKey: 'name',
        cell: ({ row }) => {
          return (
            <a
              href={`/organizations/${row.original.uuid}`}
              onClick={(e) => {
                if (e.ctrlKey || e.metaKey) {
                  e.stopPropagation();
                } else {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate({ to: `/organizations/${row.original.uuid}` });
                }
              }}
              className="link"
            >
              {row.original.name}
            </a>
          );
        },
      },
      {
        header: 'Organization Level',
        accessorKey: 'organizationLevel',
        cell: ({ getValue }) => {
          const level = getValue() as string | null;
          return <span>{level || 'N/A'}</span>;
        },
      },
      {
        header: 'Role',
        accessorKey: 'role',
        cell: ({ getValue }) => {
          const role = getValue() as string;
          return <span className="capitalize">{role}</span>;
        },
      },
      {
        header: 'Manager',
        accessorKey: 'managerName',
        cell: ({ getValue }) => {
          const manager = getValue() as string | null;
          return <span>{manager || 'N/A'}</span>;
        },
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ getValue }) => {
          const status = getValue() as string;
          const badgeType =
            status === 'activated'
              ? BADGE_TYPES.SUCCESS
              : status === 'deactivated'
              ? BADGE_TYPES.DANGER
              : BADGE_TYPES.WARNING;
          return (
            <Badge
              text={status.charAt(0).toUpperCase() + status.slice(1)}
              type={badgeType}
            />
          );
        },
      },
      {
        header: 'Date Added',
        accessorKey: 'dateAdded',
        cell: ({ getValue }) => {
          return <DateCell value={getValue() as string} format="date" />;
        },
      },
      {
        header: 'Group eNPS',
        accessorKey: 'groupEnps',
        cell: ({ getValue }) => {
          const enps = getValue() as number | null;

          // If enps is null or undefined, show "-"
          if (enps == null) return <span>-</span>;

          const color = getEnpsScoreColor(enps);

          return (
            <span
              style={{
                color: color ?? undefined,
                fontWeight: color ? 600 : undefined,
              }}
            >
              {enps}
            </span>
          );
        },
      },
      {
        header: 'Last eNPS',
        accessorKey: 'lastEnps',
        cell: ({ getValue }) => {
          const lastEnps = getValue() as number | null;
          if (lastEnps == null) return <span>-</span>;
          return (
            <span className={getEnpsScoreClasses(lastEnps)}>{lastEnps}</span>
          );
        },
      },
      {
        header: 'Route Surveys',
        accessorKey: 'routeSurveysCount',
        cell: ({ getValue }) => {
          const count = getValue() as number | null;
          return <span>{count !== null ? count : '-'}</span>;
        },
      },
      {
        header: 'Avg Survey Score',
        accessorKey: 'avgSurveyScore',
        cell: ({ getValue }) => {
          const score = getValue() as number | null;
          if (score === null) return '-';
          const isLow = score < SURVEY_POSITIVE_SCORE_THRESHOLD;
          return (
            <span className={isLow ? 'text-danger-dark-1 font-semibold' : ''}>
              {score}
            </span>
          );
        },
      },
      {
        header: 'Incident Reports',
        accessorKey: 'incidentReportsCount',
        cell: ({ getValue }) => {
          const count = getValue() as number | null;
          return <span>{count !== null ? count : '-'}</span>;
        },
      },
      {
        header: 'Comments',
        accessorKey: 'commentsCount',
        cell: ({ getValue }) => {
          const count = getValue() as number | null;
          return <span>{count !== null ? count : '-'}</span>;
        },
      },
    ];

    return baseColumns;
  }, []);

  // Navigate to user detail page on row click
  const handleUserView = (row: { original: UserTableItem }) => {
    const uuid = row.original.uuid;
    navigate({ to: `/organizations/${uuid}` });
  };

  // Handle search input change
  const handleSearchChange = (searchText: string) => {
    setPaginationState((prev) => ({
      ...prev,
      searchText,
      page: 1,
    }));
  };

  // Reset filters to default values
  const handleResetFilters = () => {
    setFilters({
      account: USER_FILTER_VALUES.ACCOUNT.ALL,
      orgLevel: getDefaultOrgLevel(),
      role: USER_FILTER_VALUES.ROLE.ALL,
      status: USER_FILTER_VALUES.STATUS.ALL,
    });
  };

  // Check if any filter is active (comparing against role-based defaults)
  const hasActiveFilters = useMemo(() => {
    const defaultOrgLevel = getDefaultOrgLevel();

    return (
      filters.account !== USER_FILTER_VALUES.ACCOUNT.ALL ||
      filters.orgLevel !== defaultOrgLevel ||
      filters.role !== USER_FILTER_VALUES.ROLE.ALL ||
      filters.status !== USER_FILTER_VALUES.STATUS.ALL
    );
  }, [filters, user?.role, user?.hierarchyLevel]);

  // Handle download as CSV
  const [isDownloading, setIsDownloading] = useState(false);
  const handleDownloadAsCsv = async () => {
    try {
      setIsDownloading(true);
      await downloadUsersAsCsv({
        sortBy: paginationState.sortBy,
        sortDirection: paginationState.direction,
        searchText: paginationState.searchText,
        accountUuid:
          filters.account !== USER_FILTER_VALUES.ACCOUNT.ALL
            ? filters.account
            : undefined,
        organizationLevelUuid:
          filters.orgLevel !== USER_FILTER_VALUES.ORGANIZATION_LEVEL.ALL
            ? filters.orgLevel
            : undefined,
        role:
          filters.role !== USER_FILTER_VALUES.ROLE.ALL
            ? filters.role
            : undefined,
        status:
          filters.status !== USER_FILTER_VALUES.STATUS.ALL
            ? filters.status
            : undefined,
      });
    } catch (error) {
      addToast('Failed to download users. Please try again.', {
        type: TOAST_TYPES.ERROR,
      });
      setIsDownloading(false);
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle add user click
  const handleAddUserClick = () => {
    const selectedAccount =
      filters.account !== USER_FILTER_VALUES.ACCOUNT.ALL
        ? filters.account
        : undefined;
    navigate({
      to: '/organizations/add',
      search: { account: selectedAccount },
    });
  };

  const handleBulkAddUsersClick = () => {
    const selectedAccount =
      filters.account !== USER_FILTER_VALUES.ACCOUNT.ALL
        ? filters.account
        : undefined;
    navigate({
      to: '/organizations/bulk-provision',
      search: { account: selectedAccount },
    });
  };

  return (
    <BaseLayout
      header={{
        headerText: 'Organization',
        headerChildren: (
          <div className="button-container-right">
            <Authorizor allowedRoles={[ROLES.ADMINISTRATOR]}>
              <Button
                variant="basic-secondary"
                size="medium"
                onClick={handleBulkAddUsersClick}
              >
                <Upload size={16} />
                Bulk Provision
              </Button>
            </Authorizor>
            <Authorizor
              allowedRoles={[
                ROLES.ADMINISTRATOR,
                ROLES.MANAGER,
                ROLES.EXECUTIVE,
              ]}
            >
              <Button
                variant="basic-primary"
                size="medium"
                onClick={handleAddUserClick}
              >
                <Add size={16} />
                {user?.role == ROLES.ADMINISTRATOR ? 'Add User' : 'Add Driver'}
              </Button>
            </Authorizor>
          </div>
        ),
      }}
      breadcrumbs={{
        showBreadcrumbs: false,
      }}
    >
      {/* Filters card */}
      <Card className="mb-6">
        <CardContent>
          {/* Dropdowns Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <Authorizor allowedRoles={[ROLES.ADMINISTRATOR]}>
              <SelectDropdown
                key={`account-filter-${accounts.length}`}
                id="account-filter"
                placeholder="Account"
                value={filters.account}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    account: e.target.value,
                  }))
                }
                isActive={filters.account !== USER_FILTER_VALUES.ACCOUNT.ALL}
              >
                <option value={USER_FILTER_VALUES.ACCOUNT.ALL}>
                  All Accounts
                </option>
                {accounts.map((account) => (
                  <option key={account.uuid} value={account.uuid}>
                    {account.name}
                  </option>
                ))}
              </SelectDropdown>
            </Authorizor>

            {/* Organization Level Filter */}
            <SelectDropdown
              key={`org-level-filter-${hierarchyOptions.length}`}
              id="organization-level-filter"
              placeholder="Organization Level"
              value={filters.orgLevel}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  orgLevel: e.target.value,
                }))
              }
              isActive={filters.orgLevel !== getDefaultOrgLevel()}
              disabled={
                userIsAuthorized(user?.role, [ROLES.ADMINISTRATOR]) &&
                filters.account === USER_FILTER_VALUES.ACCOUNT.ALL
              }
            >
              <option value={USER_FILTER_VALUES.ORGANIZATION_LEVEL.ALL}>
                All Organization Levels
              </option>
              {hierarchyOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectDropdown>

            {/* Role Filter */}
            <SelectDropdown
              id="role-filter"
              placeholder="Role"
              value={filters.role}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  role: e.target.value,
                }))
              }
              isActive={filters.role !== USER_FILTER_VALUES.ROLE.ALL}
            >
              <option value={USER_FILTER_VALUES.ROLE.ALL}>All Roles</option>
              {userIsAuthorized(user?.role, [ROLES.ADMINISTRATOR]) && (
                <option value={USER_FILTER_VALUES.ROLE.ADMINISTRATOR}>
                  Administrator
                </option>
              )}
              <option value={USER_FILTER_VALUES.ROLE.DRIVER}>Driver</option>
              <option value={USER_FILTER_VALUES.ROLE.EXECUTIVE}>
                Executive
              </option>
              <option value={USER_FILTER_VALUES.ROLE.MANAGER}>Manager</option>
              <option value={USER_FILTER_VALUES.ROLE.STAFF}>Staff</option>
            </SelectDropdown>

            {/* Status Filter */}
            <SelectDropdown
              id="status-filter"
              placeholder="Status"
              value={filters.status}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  status: e.target.value,
                }))
              }
              isActive={filters.status !== USER_FILTER_VALUES.STATUS.ALL}
            >
              <option value={USER_FILTER_VALUES.STATUS.ALL}>
                All Statuses
              </option>
              <option value={USER_FILTER_VALUES.STATUS.PROVISIONED}>
                Provisioned
              </option>
              <option value={USER_FILTER_VALUES.STATUS.ACTIVATED}>
                Activated
              </option>
              <option value={USER_FILTER_VALUES.STATUS.DEACTIVATED}>
                Deactivated
              </option>
            </SelectDropdown>

            {/* Search Input */}
            <SearchInput
              id="users-search"
              value={paginationState.searchText}
              onChange={handleSearchChange}
              placeholder="Search by name..."
              isActive={paginationState.searchText.length > 0}
            />
          </div>

          {/* Action Buttons Row */}
          <div className="button-container-right">
            <Button
              variant="outline-primary"
              size="small"
              onClick={handleDownloadAsCsv}
              disabled={isDownloading}
            >
              {isDownloading && <Spinner size="small" />}
              <Download size={16} />
              Download as CSV
            </Button>
            {hasActiveFilters && (
              <Button
                variant="outline-secondary"
                size="small"
                onClick={handleResetFilters}
              >
                Reset Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table card */}
      <Card>
        <CardContent>
          <Table
            id="users-table"
            columns={columns}
            tableData={{
              data: data,
              totalCount: totalRowsCount,
              pageCount: pageCount,
              currentPageIndex: paginationState.page,
              currentPageSize: paginationState.pageSize,
            }}
            isDataLoading={isLoading}
            sortConfig={{
              field: paginationState.sortBy,
              order: paginationState.direction,
            }}
            searchText={paginationState.searchText}
            onRowClick={handleUserView}
            setPaginationState={setPaginationState}
          />
        </CardContent>
      </Card>
    </BaseLayout>
  );
}
